// Records Security Plus activity of this browser for the current account and
// restaurant (see utils/securityActivity). Recording never blocks or fails
// the action itself; delivery trouble is shown once per page session.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AuthContext } from './AuthContext';
import {
  ActivityCategory,
  ActivityDetails,
  ActivityEvent,
  ActivityOutcome,
  AuthorizationMethod,
  randomId,
  sanitizeDetails,
} from 'utils/securityActivity/events';
import { deviceInfo, installationId } from 'utils/securityActivity/device';
import { createOutbox, DeliveryStatus, Outbox } from 'utils/securityActivity/outbox';
import { firestoreTransport } from 'utils/securityActivity/transport';

const RETRY_EVERY_MS = 60 * 1000;

export type ActivityRecord = {
  category: ActivityCategory;
  action: string;
  permission: string | null;
  outcome: ActivityOutcome;
  method: AuthorizationMethod | null;
  pinProfile: string | null;
  pinSessionId?: string | null;
  authorizationId: string | null;
} & ActivityDetails;

type Context = {
  record: (activity: ActivityRecord) => void;
  delivery: DeliveryStatus;
  installationId: string | null;
};

const NO_DELIVERY: DeliveryStatus = {
  pending: 0,
  delayed: 0,
  volatile: 0,
  rejected: 0,
  unconfirmed: 0,
  dropped: 0,
};

/** The browser storage, or null where reading it already throws (blocked storage). */
const browserStorage = () => {
  try {
    const storage = window.localStorage;
    void storage.length;
    return storage;
  } catch {
    return null;
  }
};

export const SecurityActivityContext = createContext<Context>({
  record: () => {},
  delivery: NO_DELIVERY,
  installationId: null,
});

let sharedOutbox: Outbox | null = null;
const browserOutbox = () =>
  (sharedOutbox =
    sharedOutbox ?? createOutbox({ storage: browserStorage(), send: firestoreTransport }));

type Props = {
  restaurantId: string | null;
  onDeliveryTrouble?: (status: DeliveryStatus) => void;
  outbox?: Outbox;
  children?: React.ReactNode;
};

const SecurityActivityProvider = ({
  restaurantId,
  onDeliveryTrouble,
  outbox: givenOutbox,
  children,
}: Props) => {
  const { uid } = useContext(AuthContext);
  const outbox = useMemo(() => givenOutbox ?? browserOutbox(), [givenOutbox]);
  const installation = useMemo(() => installationId(), []);
  const device = useMemo(() => deviceInfo(), []);
  const [delivery, setDelivery] = useState<DeliveryStatus>(NO_DELIVERY);
  const troubleShown = useRef(false);
  const troubleHandler = useRef(onDeliveryTrouble);
  troubleHandler.current = onDeliveryTrouble;

  useEffect(() => {
    if (!uid) return;
    outbox.forgetOtherAccounts(uid);

    const refresh = () => {
      const status = outbox.status(uid);
      // Every gated component reads this context: re-render only on a real change.
      setDelivery((current) =>
        JSON.stringify(current) === JSON.stringify(status) ? current : status
      );
      const trouble =
        status.delayed || status.volatile || status.rejected || status.unconfirmed || status.dropped;
      if (!troubleShown.current && trouble) {
        troubleShown.current = true;
        troubleHandler.current?.(status);
      }
    };
    const flush = () => void outbox.flush(uid).then(refresh);

    const unsubscribe = outbox.subscribe(refresh);
    flush();
    window.addEventListener('online', flush);
    const timer = window.setInterval(flush, RETRY_EVERY_MS);
    refresh();

    return () => {
      unsubscribe();
      window.removeEventListener('online', flush);
      window.clearInterval(timer);
    };
  }, [uid, outbox]);

  const record = useCallback(
    (activity: ActivityRecord) => {
      if (!uid || !restaurantId) return;
      try {
        // Field by field: the log's rules accept exactly these fields.
        const event: ActivityEvent = {
          category: activity.category,
          action: activity.action,
          permission: activity.permission,
          outcome: activity.outcome,
          method: activity.method,
          pinProfile: activity.pinProfile,
          pinSessionId: activity.pinSessionId ?? null,
          authorizationId: activity.authorizationId,
          ...sanitizeDetails(activity),
          eventId: randomId(),
          restaurantId,
          accountUid: uid,
          installationId: installation,
          device,
          clientTime: Date.now(),
        };
        outbox.add(event);
        void outbox.flush(uid);
      } catch (error) {
        // The log is best effort: the action it describes goes on regardless.
        console.error('security activity not recorded', error);
      }
    },
    [uid, restaurantId, outbox, installation, device]
  );

  const value = useMemo(
    () => ({ record, delivery, installationId: installation }),
    [record, delivery, installation]
  );

  return (
    <SecurityActivityContext.Provider value={value}>{children}</SecurityActivityContext.Provider>
  );
};

export default SecurityActivityProvider;
